import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import SearchIcon from '@mui/icons-material/Search'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  InputAdornment,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { twinsConversationViewed, twinsDeleteConversation } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import {
  Twin,
  TwinContextSource,
  TwinConversation,
  TwinConversationGroup,
  TwinMessage,
  TwinsConversationsReviewRequests,
} from 'api/model'
import { COLORS } from 'app-constants'
import { Messages, RecentConversationItem } from 'components/Conversations'
import AddConversationPopover from 'components/JelledBots/AddConversationPopover'
import PillSelector from 'components/PillSelector'
import ProfilePhoto from 'components/ProfilePhoto'
import ViewTabs from 'components/ViewTabs/ViewTabs'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import {
  useFetchConversationCounts,
  useFetchConversationsGroupedByTwin,
  useFetchConversationsWithTwin,
  useFetchRecentConversations,
  useFetchTwinConversation,
} from 'hooks/use-conversation'
import { useFetchConversationMessages } from 'hooks/use-messages'
import { useActiveOrganization } from 'hooks/use-organization'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { SetterOrUpdater, useRecoilValue, useSetRecoilState } from 'recoil'
import { authenticatedUserAtom, toastAtom } from 'state'
import { conversationsGroupedByTwinAtom, recentConversationsAtom } from 'state/conversations'
import { myTwinAtom } from 'state/my-twin'
import { twinsAtom } from 'state/twins'
import { useInterval } from 'usehooks-ts'
import { formatTimestamp } from 'utils'

enum ViewType {
  'All' = 'All',
  'Flagged' = 'Flagged',
  'Ongoing' = 'Ongoing',
}

export const groupConversationsByDay = (recentConversations: TwinConversationGroup[]) => {
  const groupedConversationsByDay: Record<string, TwinConversationGroup[]> = {}

  recentConversations.forEach((rc) => {
    const date = new Date(rc.twin_conversations[0].date_updated || rc.twin_conversations[0].date_created)

    // safari needs time stamp to be in the format of mm/dd/yyyy
    // we need the month increment because date.getMonth returns 0 for January but we need 1 when formatting a timestamp in the format of 1/dd/yyyy
    const dateKey = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`
    if (!groupedConversationsByDay[dateKey]) {
      groupedConversationsByDay[dateKey] = []
    }
    groupedConversationsByDay[dateKey].push(rc)
  })

  return groupedConversationsByDay
}

const TwinConversationCard = ({
  twinConversation,
  setConversationId,
  conversationId,
  onDeleteConversationClick,
}: {
  twinConversation: TwinConversation
  setConversationId: (conversationId: string) => void
  conversationId: string
  onDeleteConversationClick: (event: React.SyntheticEvent, conversationId: string) => void
}) => {
  const [activeOrganization] = useActiveOrganization()
  const navigate = useNavigate()
  const theme = useTheme()
  const setToast = useSetRecoilState(toastAtom)
  const setRecentConversations = useSetRecoilState(recentConversationsAtom)
  const setConversationsGroupedByTwin = useSetRecoilState<TwinConversationGroup[]>(conversationsGroupedByTwinAtom)

  const [isHovered, setIsHovered] = useState<boolean>(false)

  const isSelected = conversationId === twinConversation.id

  // there are two scenarios - light / dark mode as well as whether or not the card is selected
  const getTextColor = () => {
    // in light mode, the text will always be black
    if (theme.palette.mode === 'light') {
      return COLORS.GRAY_0
    } else {
      // in dark mode, when the card is selected / hovered, the text will be black
      const isSelected = conversationId === twinConversation.id
      if (isHovered || isSelected) {
        return COLORS.GRAY_0
      }
    }
    return COLORS.PURE_WHITE
  }

  const getHasUnreadEdits = () => {
    // case 1: if date_viewed is empty, then there are unread edits
    if (!twinConversation.date_viewed) {
      return true
    }

    // case 2: if date_viewed is before date_updated, then there are unread edits
    const dateViewed = new Date(twinConversation.date_viewed)
    const dateUpdated = new Date(twinConversation.date_updated ?? '')
    if (dateViewed < dateUpdated) {
      return true
    }

    return false
  }

  // should call conversation viewed endpoint
  const updateConversationViewedAt = async () => {
    if (!activeOrganization) {
      return
    }

    try {
      const conversation = twinConversation

      const response = await twinsConversationViewed(activeOrganization, conversation.id, {
        twin_id: conversation?.twin_id ?? '',
      })

      // should update the conversation in recoil in recent conversations
      setRecentConversations((prev) => {
        const updatedRecentConversations = prev.map((rc) => {
          if (rc.twin_conversations.find((c) => c.id === conversation.id)) {
            return {
              ...rc,
              twin_conversations: rc.twin_conversations.map((c) => {
                if (c.id === conversation.id) {
                  return response.data
                }
                return c
              }),
            }
          }
          return rc
        })
        return updatedRecentConversations
      })

      // should update the conversation in recoil in conversations grouped by twin
      setConversationsGroupedByTwin((prev) => {
        if (!prev) {
          return []
        }

        const updatedConversationsGroupedByTwin = prev.map((c) => {
          return {
            ...c,
            twin_conversations: c.twin_conversations.map((tc) => {
              if (tc.id === conversation.id) {
                return response.data
              }
              return tc
            }),
          }
        })

        return updatedConversationsGroupedByTwin
      })
    } catch {
      setToast({
        title: 'Failed to update conversation viewed at',
        severity: 'error',
      })
    }
  }

  return (
    <Stack mb={2} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      <Stack
        borderRadius="8px"
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        onClick={() => {
          setConversationId(twinConversation.id)
          navigate(`/jelledbots?twinId=${twinConversation.twin_id}`)
          // whenever we click on a twin conversation card, we should update the conversation viewed at property
          updateConversationViewedAt()
        }}
        sx={{
          cursor: 'pointer',
          py: 1,
          px: 2,
          backgroundColor: isSelected ? COLORS.GRAY_8 : 'transparent',
          height: '48px',
          '&:hover': {
            backgroundColor: COLORS.GRAY_8,
          },
        }}>
        <Stack>
          <Typography
            variant="pS"
            sx={{
              width: '179px',
              color: getTextColor(),
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
            {twinConversation.title}
          </Typography>
        </Stack>

        {isHovered ? (
          <IconButton
            onClick={(e) => onDeleteConversationClick(e, twinConversation.id)}
            sx={{
              padding: '8px',
              ml: -1.5,
            }}>
            <DeleteOutlinedIcon
              sx={{
                fill: COLORS.GRAY_3,
              }}
            />
          </IconButton>
        ) : getHasUnreadEdits() ? (
          <Stack
            sx={{
              height: '10px',
              width: '10px',
              backgroundColor: COLORS.JELLED_PRIMARY_BLUE,
              borderRadius: '50%',
              mr: 1.5,
            }}
          />
        ) : null}
      </Stack>
    </Stack>
  )
}

const TwinConversations = ({
  conversationId,
  setConversationId,
}: {
  conversationId: string
  setConversationId: (conversationId: string) => void
}) => {
  const [activeOrganization] = useActiveOrganization()
  const setToast = useSetRecoilState(toastAtom)

  const [updatedSince, setUpdatedSince] = useState<string>('')

  const { conversationsGroupedByTwin, isLoading, setConversationsGroupedByTwin, refetchConversationsGroupedByTwin } =
    useFetchConversationsGroupedByTwin({
      groupByTwin: true,
      shouldFetchOnMount: true,
    })

  useEffect(() => {
    if (conversationsGroupedByTwin.length !== 0) {
      setUpdatedSince(new Date().toISOString())
    }
  }, [conversationsGroupedByTwin])

  // every minute, we should refetch conversations with the latest updatedSince value
  useInterval(() => {
    refetchConversationsGroupedByTwin(updatedSince)
    setUpdatedSince(new Date().toISOString())
  }, 1000 * 60)

  const [searchText, setSearchText] = useState<string>('')
  const onSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const [pendingDeleteConversationId, setPendingDeleteConversationId] = useState<string>('')

  const [isDeletingConversation, setIsDeletingConversation] = useState<boolean>(false)
  const onDeleteConversationConfirmed = async () => {
    setIsDeletingConversation(true)

    if (!activeOrganization) {
      setIsDeletingConversation(false)
      return null
    }

    try {
      await twinsDeleteConversation(activeOrganization, pendingDeleteConversationId, {
        twin_id: conversationsGroupedByTwin[0].twin.id,
      })
      // we should also remove that conversation from recoil
      setConversationsGroupedByTwin?.((prev) => {
        const updatedConversationsGroupedByTwin = prev.map((c) => {
          const updatedTwinConversations = c.twin_conversations.filter((tc) => {
            return tc.id !== pendingDeleteConversationId
          })

          return {
            ...c,
            twin_conversations: updatedTwinConversations,
          }
        })

        // filter out the conversations grouped by twin that don't have any conversations
        const filteredConversationsGroupedByTwin = updatedConversationsGroupedByTwin.filter((c) => {
          return c.twin_conversations.length !== 0
        })

        return filteredConversationsGroupedByTwin
      })

      setPendingDeleteConversationId('')
      setConversationId('')
      setToast({
        title: 'Conversation deleted',
        severity: 'success',
      })
    } catch (error) {
      setToast({
        title: 'Failed to delete conversation',
        severity: 'error',
      })
    } finally {
      setIsDeletingConversation(false)
    }
  }

  const onDeleteConversationClick = (event: React.SyntheticEvent, conversationId: string) => {
    setPendingDeleteConversationId(conversationId)
    event?.stopPropagation()
  }

  const filteredConversationsGroupedByTwin = useMemo(() => {
    return conversationsGroupedByTwin.filter((c) => {
      return c.twin.name.toLowerCase().includes(searchText.toLowerCase())
    })
  }, [conversationsGroupedByTwin, searchText])

  // TODO: add loading state
  if (isLoading) {
    return null
  }

  return (
    <Stack width="100%">
      <TextField
        placeholder="Search for twin"
        value={searchText}
        onChange={onSearchTextChange}
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon
                sx={{
                  color: COLORS.GRAY_3,
                  marginRight: '-10px',
                }}
              />
            </InputAdornment>
          ),
        }}
        sx={{
          marginBottom: '16px',
          input: {
            color: 'secondary.main',
          },
          '& ::placeholder': {
            color: 'secondary.main',
          },
        }}
      />

      {filteredConversationsGroupedByTwin.map((c) => (
        <Stack key={c.twin.id}>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <ProfilePhoto src={c.twin.picture} size="32px" name={c.twin.name || c.twin.user.email} />
            <Typography
              variant="h6"
              mb={1}
              sx={{
                color: COLORS.GRAY_0,
              }}>
              {c.twin.name || c.twin.user.email}
            </Typography>
          </Stack>

          {c.twin_conversations.map((tc) => (
            <TwinConversationCard
              key={tc.id}
              twinConversation={tc}
              setConversationId={setConversationId}
              conversationId={conversationId}
              onDeleteConversationClick={onDeleteConversationClick}
            />
          ))}
        </Stack>
      ))}
      <Dialog open={!!pendingDeleteConversationId}>
        <DialogTitle>Are you sure you want to delete this conversation?</DialogTitle>
        <DialogActions>
          <Button
            onClick={() => {
              setPendingDeleteConversationId('')
            }}
            disabled={isDeletingConversation}
            sx={{
              color: COLORS.GRAY_0,
            }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={onDeleteConversationConfirmed}
            disabled={isDeletingConversation}>
            Yes, delete it
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

const ConversationsSidebarContent = ({
  verboseOutput,
  setVerboseOutput,
  contextSources,
  setContextSources,
  conversationId,
  setConversationId,
}: {
  verboseOutput: string
  setVerboseOutput: React.Dispatch<React.SetStateAction<string>>
  contextSources: TwinContextSource[]
  setContextSources: React.Dispatch<React.SetStateAction<TwinContextSource[]>>
  conversationId: string
  setConversationId: React.Dispatch<React.SetStateAction<string>>
}) => {
  const isMobile = useCheckMobileScreen()
  const pills = ['Recents', 'Twin']
  const [selectedPillIndex, setSelectedPillIndex] = useState<number>(0)

  // we need to keep of updatedSince value so that we can refetch with this value
  // the reason we need this is to see if we have newly updated conversations where some messages are stale
  const [updatedSince, setUpdatedSince] = useState<string>('')

  const { recentConversations, isLoading, setRecentConversations, refetchRecentConversations } =
    useFetchRecentConversations({
      shouldFetchOnMount: true,
    })

  useEffect(() => {
    if (recentConversations.length !== 0) {
      setUpdatedSince(new Date().toISOString())
    }
  }, [recentConversations])

  // every minute, we should refetch conversations with the latest updatedSince value
  useInterval(() => {
    refetchRecentConversations(updatedSince)
    setUpdatedSince(new Date().toISOString())
  }, 1000 * 60)

  const groupedConversationsByDay = useMemo(() => {
    return groupConversationsByDay(recentConversations)
  }, [recentConversations])
  const days = useMemo(() => {
    return Object.keys(groupedConversationsByDay)
  }, [groupedConversationsByDay])

  const setConversationsGroupedByTwin = useSetRecoilState<TwinConversationGroup[]>(conversationsGroupedByTwinAtom)

  const hydrateRecentConversations = (twin: Twin, twinConversation: TwinConversation) => {
    setRecentConversations((prev) => {
      // add the new conversation to the top of the list
      return [{ twin, twin_conversations: [twinConversation] }, ...prev]
    })
  }

  const hydrateConversationsGroupedByTwin = (twin: Twin, twinConversation: TwinConversation) => {
    setConversationsGroupedByTwin((prev) => {
      if (!prev) {
        return []
      }

      // if prev was an empty array, we should return the updatedConversationsGroupedByTwin
      if (prev.length === 0) {
        return [
          {
            twin,
            twin_conversations: [twinConversation],
          },
        ]
      }

      const updatedConversationsGroupedByTwin = prev.map((c) => {
        if (c.twin.id === twin.id) {
          return {
            ...c,
            twin_conversations: [twinConversation, ...c.twin_conversations],
          }
        }
        return c
      })

      return updatedConversationsGroupedByTwin
    })
  }

  // hydration is required to show the newly created conversation in the sidebar regardless of the view type
  const onConversationAdded = (
    twin: Twin,
    twinConversation: TwinConversation,
    verboseOutput: string,
    contextSources?: TwinContextSource[]
  ) => {
    hydrateRecentConversations(twin, twinConversation)
    hydrateConversationsGroupedByTwin(twin, twinConversation)
    // we want the latest conversation to be the one that is selected
    setConversationId(twinConversation.id)
    setVerboseOutput(verboseOutput)
    if (contextSources) {
      setContextSources(contextSources)
    }
  }

  // if there is no conversation selected and if there is a twin id in the url, then we should show the new chat sidebar item
  const [searchParams] = useSearchParams()
  const twinIdFromUrl = searchParams.get('twinId')
  const shouldShowNewChat = !conversationId && twinIdFromUrl
  const twin = useRecoilValue(twinsAtom).find((t) => t.id === twinIdFromUrl)

  // logic for the AddConversationPopover component
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const open = Boolean(anchorEl)
  const handleClose = () => {
    setAnchorEl(null)
  }
  const id = open ? 'simple-popover' : undefined

  if (isLoading) {
    return <CircularProgress size="24px" thickness={4} style={{ color: COLORS.JELLED_PRIMARY_BLUE }} />
  }

  return (
    <Stack spacing={3}>
      <Button
        className="add-conversation-button"
        variant="contained"
        color="primary"
        onClick={(e: any) => {
          const buttonEl = document.querySelector('.add-conversation-button')
          if (buttonEl) {
            setAnchorEl(buttonEl as HTMLButtonElement)
          }
        }}
        startIcon={
          <EditOutlinedIcon
            sx={{
              height: '16px',
              width: '16px',
            }}
          />
        }>
        New conversation
      </Button>

      <PillSelector pills={pills} selectedIndex={selectedPillIndex} onSelectPill={setSelectedPillIndex} />

      {shouldShowNewChat && twin && (
        <Stack
          p="12px 15px"
          mb={4}
          borderRadius="8px"
          sx={{
            backgroundColor: COLORS.GRAY_8,
          }}>
          <Typography variant="pSM">New message</Typography>
        </Stack>
      )}

      {selectedPillIndex === 0 && (
        <Stack>
          {days.map((day) => (
            <RecentConversationItem
              key={day}
              day={day}
              groupedConversationsByDay={groupedConversationsByDay}
              conversationId={conversationId}
              setConversationId={setConversationId}
              setRecentConversations={setRecentConversations}
            />
          ))}
        </Stack>
      )}

      {selectedPillIndex === 1 && (
        <TwinConversations conversationId={conversationId} setConversationId={setConversationId} />
      )}

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: isMobile ? 'bottom' : 'top',
          horizontal: isMobile ? 'center' : 'right',
        }}
        transformOrigin={{
          vertical: isMobile ? 'top' : 'top',
          horizontal: isMobile ? 'center' : 'left',
        }}
        sx={{
          ml: isMobile ? 0 : 1,
        }}>
        <AddConversationPopover
          setConversationId={setConversationId}
          onConversationAdded={onConversationAdded}
          onClose={handleClose}
        />
      </Popover>
    </Stack>
  )
}

function formatViewTypeAsReviewRequest(viewType: ViewType) {
  if (viewType === ViewType.All) {
    return TwinsConversationsReviewRequests.all
  } else if (viewType === ViewType.Flagged) {
    return TwinsConversationsReviewRequests.with
  } else if (viewType === ViewType.Ongoing) {
    return TwinsConversationsReviewRequests.without
  }
  return TwinsConversationsReviewRequests.all
}

const SortDirectionButton = ({
  isTimestampAscending,
  setIsTimestampAscending,
}: {
  isTimestampAscending: boolean
  setIsTimestampAscending: SetterOrUpdater<boolean>
}) => {
  return (
    <Button
      color="secondary"
      onClick={() => {
        if (isTimestampAscending) {
          setIsTimestampAscending(false)
        } else {
          setIsTimestampAscending(true)
        }
      }}
      sx={{
        cursor: 'pointer',
        my: 2,
        width: 'fit-content',
      }}>
      {isTimestampAscending ? (
        <KeyboardArrowUpIcon
          onClick={() => setIsTimestampAscending(false)}
          sx={{
            fill: COLORS.GRAY_0,
          }}
        />
      ) : (
        <KeyboardArrowDownIcon
          onClick={() => setIsTimestampAscending(true)}
          sx={{
            fill: COLORS.GRAY_0,
          }}
        />
      )}
      <Typography variant="h6">{isTimestampAscending ? 'Oldest' : 'Newest'}</Typography>
    </Button>
  )
}

const SidebarItem = ({
  conversationId,
  setConversationId,
  conversation,
  onDeleteConversation,
  setConversationsWithTwin,
  refetchConversationCounts,
}: {
  conversationId: string
  setConversationId: React.Dispatch<React.SetStateAction<string>>
  conversation: TwinConversation
  onDeleteConversation: (event: React.SyntheticEvent, conversationId: string) => void
  setConversationsWithTwin: SetterOrUpdater<TwinConversationGroup[]>
  refetchConversationCounts: () => void
}) => {
  const [activeOrganization] = useActiveOrganization()
  const user = useRecoilValue(authenticatedUserAtom)
  const navigate = useNavigate()
  const setRecentConversations = useSetRecoilState(recentConversationsAtom)
  const setConversationsGroupedByTwin = useSetRecoilState<TwinConversationGroup[]>(conversationsGroupedByTwinAtom)
  const setToast = useSetRecoilState(toastAtom)

  const [isHovering, setIsHovering] = useState<boolean>(false)
  const onMouseEnter = () => {
    setIsHovering(true)
  }

  const onMouseLeave = () => {
    setIsHovering(false)
  }

  const isSelected = conversation.id === conversationId
  const onSidebarItemClick = async () => {
    setConversationId(conversation.id)
    navigate(`/jelledbots?isReviews=true&twinId=${conversation.twin_id}`)
    await updateConversationViewedAt()
  }

  // should call conversation viewed endpoint
  const updateConversationViewedAt = async () => {
    if (!activeOrganization) {
      return
    }

    try {
      const response = await twinsConversationViewed(activeOrganization, conversation.id, {
        twin_id: conversation.twin_id,
        as_owner: true,
      })

      // should update the conversation in recoil in recent conversations
      setRecentConversations((prev) => {
        const updatedRecentConversations = prev.map((rc) => {
          if (rc.twin_conversations.find((c) => c.id === conversation.id)) {
            return {
              ...rc,
              twin_conversations: rc.twin_conversations.map((c) => {
                if (c.id === conversation.id) {
                  return response.data
                }
                return c
              }),
            }
          }
          return rc
        })
        return updatedRecentConversations
      })

      // should update the conversation in recoil in conversations grouped by twin
      setConversationsGroupedByTwin((prev) => {
        if (!prev) {
          return []
        }

        const updatedConversationsGroupedByTwin = prev.map((c) => {
          return {
            ...c,
            twin_conversations: c.twin_conversations.map((tc) => {
              if (tc.id === conversation.id) {
                return response.data
              }
              return tc
            }),
          }
        })

        return updatedConversationsGroupedByTwin
      })

      setConversationsWithTwin((prev) => {
        const updatedConversationsWithTwin = prev.map((c) => {
          return {
            ...c,
            twin_conversations: c.twin_conversations.map((tc) => {
              if (tc.id === conversation.id) {
                return response.data
              }
              return tc
            }),
          }
        })

        return updatedConversationsWithTwin
      })

      // we also need to make a refetch call to update the conversation count in the navbar
      refetchConversationCounts()
    } catch {
      setToast({
        title: 'Failed to update conversation viewed at',
        severity: 'error',
      })
    }
  }

  const onDeleteClick = (e: React.SyntheticEvent, conversationId: string) => {
    onDeleteConversation(e, conversationId)
  }

  // we should show the unread indicator if the date_reviewed is null or if date_updated is less than or equal to date_reviewed
  const showUnreadIndicator = () => {
    if (!conversation.date_reviewed) {
      return true
    }

    const dateReviewed = new Date(conversation.date_reviewed ?? '')
    const dateUpdated = new Date(conversation.date_updated ?? '')
    return dateReviewed < dateUpdated
  }

  return (
    <Stack
      alignItems="center"
      p="12px"
      borderRadius="8px"
      direction="row"
      justifyContent="space-between"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onSidebarItemClick}
      sx={{
        cursor: 'pointer',
        backgroundColor: isSelected ? COLORS.GRAY_8 : COLORS.PURE_WHITE,
        '&:hover': {
          backgroundColor: COLORS.GRAY_8,
        },
      }}>
      <Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <ProfilePhoto
            src={conversation.user.picture}
            size="32px"
            name={conversation.user.name || conversation.user.email}
          />

          <Stack maxWidth="205px">
            <Typography variant="h6">{conversation.user.name}</Typography>

            <Stack direction="row" alignItems="center" spacing={1}>
              <Tooltip title={conversation.title} enterTouchDelay={0}>
                <Typography
                  variant="pS"
                  sx={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}>
                  {conversation.title}
                </Typography>
              </Tooltip>
              <Typography
                variant="pXS"
                sx={{
                  minWidth: '74px',
                }}>
                {formatTimestamp(conversation.date_created)}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Stack>

      {isHovering && user?.is_system_admin ? (
        <IconButton
          onClick={(e) => {
            onDeleteClick(e, conversation.id)
          }}>
          <DeleteOutlinedIcon />
        </IconButton>
      ) : (
        <Stack
          sx={{
            display: showUnreadIndicator() ? 'block' : 'none',
            height: '10px',
            width: '10px',
            backgroundColor: COLORS.JELLED_PRIMARY_BLUE,
            borderRadius: '50%',
            mr: 1.7,
          }}
        />
      )}
    </Stack>
  )
}

const ReviewsSidebarContent = ({
  conversationId,
  setConversationId,
  setConversation,
}: {
  conversationId: string
  setConversationId: React.Dispatch<React.SetStateAction<string>>
  setConversation: React.Dispatch<React.SetStateAction<TwinConversation | null>>
}) => {
  const [activeOrganization] = useActiveOrganization()
  const myTwin = useRecoilValue(myTwinAtom)
  const setToast = useSetRecoilState(toastAtom)

  const { refetchConversationCounts } = useFetchConversationCounts()

  const pills = Object.values(ViewType)
  const [selectedPillIndex, setSelectedPillIndex] = useState<number>(0)

  // store is timestamp ascending in local storage
  const [isTimestampAscending, setIsTimestampAscending] = useState<boolean>(
    localStorage.getItem('jelled_isTimestampAscending') === 'true'
  )

  useEffect(() => {
    localStorage.setItem('jelled_isTimestampAscending', isTimestampAscending.toString())
  }, [isTimestampAscending])

  const { conversationsWithTwin, setConversationsWithTwin } = useFetchConversationsWithTwin(
    myTwin?.id ?? '',
    formatViewTypeAsReviewRequest(pills[selectedPillIndex])
  )

  const flattedConversationsWithTwin = conversationsWithTwin.flatMap((c) => {
    return c.twin_conversations
  })

  // sort by date_created
  const sortedConversations = flattedConversationsWithTwin.sort((a, b) => {
    if (isTimestampAscending) {
      return new Date(a.date_created).getTime() - new Date(b.date_created).getTime()
    } else {
      return new Date(b.date_created).getTime() - new Date(a.date_created).getTime()
    }
  })

  const [pendingDeleteConversationId, setPendingDeleteConversationId] = useState<string>('')
  const onDeleteConversation = async (event: React.SyntheticEvent, conversationId: string) => {
    setPendingDeleteConversationId(conversationId)
    event?.stopPropagation()
  }

  const [isDeletingConversation, setIsDeletingConversation] = useState<boolean>(false)
  const onDeleteConversationConfirmed = async () => {
    setIsDeletingConversation(true)

    if (!activeOrganization || !myTwin || !pendingDeleteConversationId) {
      setIsDeletingConversation(false)
      return null
    }

    try {
      await twinsDeleteConversation(activeOrganization, pendingDeleteConversationId, {
        twin_id: myTwin?.id,
      })
      setPendingDeleteConversationId('')
      setConversationId('')
      setConversation(null)
      // we should also remove that conversation from recoil
      setConversationsWithTwin?.((prev) => {
        const updatedRecentConversations = prev.filter((rc) => {
          return rc.twin_conversations[0].id !== pendingDeleteConversationId
        })
        return updatedRecentConversations
      })
      setToast({
        title: 'Conversation deleted',
        severity: 'success',
      })
    } catch (error) {
      setToast({
        title: 'Failed to delete conversation',
        severity: 'error',
      })
    } finally {
      setIsDeletingConversation(false)
    }
  }

  return (
    <Stack>
      <PillSelector pills={pills} selectedIndex={selectedPillIndex} onSelectPill={setSelectedPillIndex} />

      <SortDirectionButton
        isTimestampAscending={isTimestampAscending}
        setIsTimestampAscending={setIsTimestampAscending}
      />

      <Stack spacing={1} mt={2}>
        {sortedConversations.map((c) => {
          return (
            <SidebarItem
              key={c.id}
              conversationId={conversationId}
              setConversationId={setConversationId}
              conversation={c}
              onDeleteConversation={onDeleteConversation}
              setConversationsWithTwin={setConversationsWithTwin}
              refetchConversationCounts={refetchConversationCounts}
            />
          )
        })}
      </Stack>

      <Dialog open={!!pendingDeleteConversationId}>
        <DialogTitle>Are you sure you want to delete this conversation?</DialogTitle>
        <DialogActions>
          <Button
            onClick={() => {
              setPendingDeleteConversationId('')
            }}
            disabled={isDeletingConversation}
            sx={{
              color: COLORS.GRAY_0,
            }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={onDeleteConversationConfirmed}
            disabled={isDeletingConversation}>
            Yes, delete it
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

// used to render the sidebar content specific to the currently selected view tab
const SidebarContent = ({
  tabIndex,
  contextSources,
  setContextSources,
  verboseOutput,
  setVerboseOutput,
  conversationId,
  setConversationId,
  setConversation,
}: {
  tabIndex: number
  contextSources: TwinContextSource[]
  setContextSources: React.Dispatch<React.SetStateAction<TwinContextSource[]>>
  verboseOutput: string
  setVerboseOutput: React.Dispatch<React.SetStateAction<string>>
  conversationId: string
  setConversationId: React.Dispatch<React.SetStateAction<string>>
  setConversation: React.Dispatch<React.SetStateAction<TwinConversation | null>>
}) => {
  switch (tabIndex) {
    case 0:
      return (
        <ConversationsSidebarContent
          contextSources={contextSources}
          setContextSources={setContextSources}
          verboseOutput={verboseOutput}
          setVerboseOutput={setVerboseOutput}
          conversationId={conversationId}
          setConversationId={setConversationId}
        />
      )

    case 1:
      return (
        <ReviewsSidebarContent
          setConversationId={setConversationId}
          conversationId={conversationId}
          setConversation={setConversation}
        />
      )
  }
  return null
}

const Sidebar = ({
  tabIndex,
  setTabIndex,
  contextSources,
  setContextSources,
  verboseOutput,
  setVerboseOutput,
  conversationId,
  setConversationId,
  setConversation,
}: {
  tabIndex: number
  setTabIndex: React.Dispatch<React.SetStateAction<number>>
  contextSources: TwinContextSource[]
  setContextSources: React.Dispatch<React.SetStateAction<TwinContextSource[]>>
  verboseOutput: string
  setVerboseOutput: React.Dispatch<React.SetStateAction<string>>
  conversationId: string
  setConversationId: React.Dispatch<React.SetStateAction<string>>
  setConversation: React.Dispatch<React.SetStateAction<TwinConversation | null>>
}) => {
  const isMobile = useCheckMobileScreen()

  return (
    <Stack
      flex="0 0 368px"
      pt={3}
      px={3}
      spacing={3}
      height="100vh"
      borderRight="1px solid #DDDDDD"
      sx={{
        backgroundColor: COLORS.PURE_WHITE,
        overflowX: 'scroll',
      }}>
      {!isMobile && <Typography variant="h4">JelledBots</Typography>}
      {!isMobile && <ViewTabs tabIndex={tabIndex} setTabIndex={setTabIndex} tabs={['Conversations', 'Reviews']} />}

      <SidebarContent
        contextSources={contextSources}
        setContextSources={setContextSources}
        verboseOutput={verboseOutput}
        setVerboseOutput={setVerboseOutput}
        tabIndex={tabIndex}
        conversationId={conversationId}
        setConversationId={setConversationId}
        setConversation={setConversation}
      />
    </Stack>
  )
}

const ConversationsContent = ({
  contextSources,
  setContextSources,
  verboseOutput,
  setVerboseOutput,
  isSubmitting,
  setIsSubmitting,
  conversation,
  isLoadingConversationMessages,
  hydrateConversationMessages,
  hydrateConversationMessage,
  conversationMessages,
  setConversationId,
  setIsCreatingNewConversation,
  hydrateRecentConversations,
  hydrateConversationsGroupedByTwin,
}: {
  contextSources: TwinContextSource[]
  setContextSources: React.Dispatch<React.SetStateAction<TwinContextSource[]>>
  verboseOutput: string
  setVerboseOutput: React.Dispatch<React.SetStateAction<string>>
  isSubmitting: boolean
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>
  conversation?: TwinConversation
  isLoadingConversationMessages: boolean
  hydrateConversationMessages: (twinMessage: TwinMessage) => void
  hydrateConversationMessage: (twinMessage: TwinMessage) => void
  conversationMessages: TwinMessage[]
  setConversationId: React.Dispatch<React.SetStateAction<string>>
  setIsCreatingNewConversation: React.Dispatch<React.SetStateAction<boolean>>
  hydrateRecentConversations: (twin: Twin, twinConversation: TwinConversation) => void
  hydrateConversationsGroupedByTwin: (twin: Twin, twinConversation: TwinConversation) => void
}) => {
  return (
    <Stack
      sx={{
        height: '100vh',
      }}>
      <Messages
        contextSources={contextSources}
        setContextSources={setContextSources}
        verboseOutput={verboseOutput}
        setVerboseOutput={setVerboseOutput}
        isSubmitting={isSubmitting}
        setIsSubmitting={setIsSubmitting}
        conversation={conversation}
        isLoadingConversationMessages={isLoadingConversationMessages}
        hydrateConversationMessages={hydrateConversationMessages}
        hydrateConversationMessage={hydrateConversationMessage}
        conversationMessages={conversationMessages}
        setConversationId={setConversationId}
        setIsCreatingNewConversation={setIsCreatingNewConversation}
        hydrateRecentConversations={hydrateRecentConversations}
        hydrateConversationsGroupedByTwin={hydrateConversationsGroupedByTwin}
      />
    </Stack>
  )
}

const ReviewsContent = ({
  contextSources,
  setContextSources,
  verboseOutput,
  setVerboseOutput,
  conversation,
  conversationMessages,
  isLoadingConversationMessages,
  setConversationId,
  hydrateConversationMessages,
  hydrateConversationMessage,
  isSubmitting,
  setIsSubmitting,
  hydrateConversationReviewRequests,
}: {
  verboseOutput: string
  setVerboseOutput: (verboseOutput: string) => void
  contextSources: TwinContextSource[]
  setContextSources: (contextSources: TwinContextSource[]) => void
  conversation?: TwinConversation
  conversationMessages: TwinMessage[]
  setConversationId?: React.Dispatch<React.SetStateAction<string>>
  isLoadingConversationMessages: boolean
  hydrateConversationMessages: (twinMessage: TwinMessage) => void
  hydrateConversationMessage: (twinMessage: TwinMessage) => void
  isSubmitting: boolean
  setIsSubmitting: (isSubmitting: boolean) => void
  hydrateConversationReviewRequests?: (reviewRequests: number) => void
}) => {
  return (
    <Stack>
      <Messages
        isReviewMode={true}
        contextSources={contextSources}
        setContextSources={setContextSources}
        verboseOutput={verboseOutput}
        setVerboseOutput={setVerboseOutput}
        conversation={conversation ?? undefined}
        conversationMessages={conversationMessages}
        isLoadingConversationMessages={isLoadingConversationMessages}
        setConversationId={setConversationId}
        hydrateConversationMessages={hydrateConversationMessages}
        hydrateConversationMessage={hydrateConversationMessage}
        isSubmitting={isSubmitting}
        setIsSubmitting={setIsSubmitting}
        hydrateConversationReviewRequests={hydrateConversationReviewRequests}
      />
    </Stack>
  )
}

const Content = ({
  tabIndex,
  contextSources,
  setContextSources,
  verboseOutput,
  setVerboseOutput,
  isSubmitting,
  setIsSubmitting,
  conversation,
  isLoadingConversationMessages,
  hydrateConversationMessages,
  hydrateConversationMessage,
  conversationMessages,
  setConversationId,
  setIsCreatingNewConversation,
  hydrateRecentConversations,
  hydrateConversationsGroupedByTwin,
}: {
  tabIndex: number
  contextSources: TwinContextSource[]
  setContextSources: React.Dispatch<React.SetStateAction<TwinContextSource[]>>
  verboseOutput: string
  setVerboseOutput: React.Dispatch<React.SetStateAction<string>>
  isSubmitting: boolean
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>
  conversation?: TwinConversation
  isLoadingConversationMessages: boolean
  hydrateConversationMessages: (twinMessage: TwinMessage) => void
  hydrateConversationMessage: (twinMessage: TwinMessage) => void
  conversationMessages: TwinMessage[]
  setConversationId: React.Dispatch<React.SetStateAction<string>>
  setIsCreatingNewConversation: React.Dispatch<React.SetStateAction<boolean>>
  hydrateRecentConversations: (twin: Twin, twinConversation: TwinConversation) => void
  hydrateConversationsGroupedByTwin: (twin: Twin, twinConversation: TwinConversation) => void
}) => {
  let content
  switch (tabIndex) {
    case 0:
      content = (
        <ConversationsContent
          contextSources={contextSources}
          setContextSources={setContextSources}
          verboseOutput={verboseOutput}
          setVerboseOutput={setVerboseOutput}
          isSubmitting={isSubmitting}
          setIsSubmitting={setIsSubmitting}
          conversation={conversation}
          isLoadingConversationMessages={isLoadingConversationMessages}
          hydrateConversationMessages={hydrateConversationMessages}
          hydrateConversationMessage={hydrateConversationMessage}
          conversationMessages={conversationMessages}
          setConversationId={setConversationId}
          setIsCreatingNewConversation={setIsCreatingNewConversation}
          hydrateRecentConversations={hydrateRecentConversations}
          hydrateConversationsGroupedByTwin={hydrateConversationsGroupedByTwin}
        />
      )
      break
    case 1:
      content = (
        <ReviewsContent
          contextSources={contextSources}
          setContextSources={setContextSources}
          verboseOutput={verboseOutput}
          setVerboseOutput={setVerboseOutput}
          isSubmitting={isSubmitting}
          setIsSubmitting={setIsSubmitting}
          conversation={conversation}
          conversationMessages={conversationMessages}
          isLoadingConversationMessages={isLoadingConversationMessages}
          setConversationId={setConversationId}
          hydrateConversationMessages={hydrateConversationMessages}
          hydrateConversationMessage={hydrateConversationMessage}
        />
      )
      break
  }

  return (
    <Stack width="100%" height="100%">
      {content}
    </Stack>
  )
}

const JelledBots = () => {
  const [searchParams] = useSearchParams()
  const isMobile = useCheckMobileScreen()
  const navigate = useNavigate()

  const [tabIndex, setTabIndex] = useState<number>(0)

  // state needed for conversations
  const [contextSources, setContextSources] = useState<TwinContextSource[]>([])
  const [verboseOutput, setVerboseOutput] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const conversationIdFromParams = searchParams.get('conversationId')
  const twinIdFromParams = searchParams.get('twinId')
  const isReviewMode = searchParams.get('isReviews')
  useEffect(() => {
    if (isReviewMode) {
      setTabIndex(1)
    } else {
      setTabIndex(0)
    }
  }, [isReviewMode])

  useEffect(() => {
    if (tabIndex === 0) {
      // navigate to /jelledbots but keep the query params in the url if they exist
      let url = '/jelledbots'

      if (twinIdFromParams) {
        url += `?twinId=${twinIdFromParams}`
        if (conversationIdFromParams) {
          url += `&conversationId=${conversationIdFromParams}`
        }
      }

      navigate(url)
    }
  }, [tabIndex, navigate, conversationIdFromParams, twinIdFromParams])

  const [conversationId, setConversationId] = useState<string>(conversationIdFromParams ?? '')
  const [isCreatingNewConversation, setIsCreatingNewConversation] = useState<boolean>(false)

  // fetch the conversation with the selected conversationId
  const { conversation, setConversation } = useFetchTwinConversation(conversationId, twinIdFromParams ?? '')

  // fetch conversation messages
  const {
    conversationMessages,
    setConversationMessages,
    isLoading: isLoadingConversationMessages,
  } = useFetchConversationMessages(conversation?.twin_id ?? '', conversationId, {
    isCreatingNewConversation,
  })

  const hydrateConversationMessages = (twinMessage: TwinMessage) => {
    // we need to update conversationMessages in recoil
    setConversationMessages((prev) => {
      return [...prev, twinMessage]
    })
  }

  // this is used when we want to update a specific twin message in recoil
  const hydrateConversationMessage = (twinMessage: TwinMessage) => {
    setConversationMessages((prev) => {
      const updatedConversationMessages = prev.map((cm) => {
        if (cm.id === twinMessage.id) {
          return twinMessage
        }
        return cm
      })
      return updatedConversationMessages
    })
  }

  const setRecentConversations = useSetRecoilState(recentConversationsAtom)
  const setConversationsGroupedByTwin = useSetRecoilState<TwinConversationGroup[]>(conversationsGroupedByTwinAtom)

  const hydrateRecentConversations = (twin: Twin, twinConversation: TwinConversation) => {
    setRecentConversations((prev) => {
      // add the new conversation to the top of the list
      return [{ twin, twin_conversations: [twinConversation] }, ...prev]
    })
  }

  const hydrateConversationsGroupedByTwin = (twin: Twin, twinConversation: TwinConversation) => {
    setConversationsGroupedByTwin((prev) => {
      if (!prev) {
        return []
      }

      // if prev was an empty array, we should return the updatedConversationsGroupedByTwin
      if (prev.length === 0) {
        return [
          {
            twin,
            twin_conversations: [twinConversation],
          },
        ]
      }

      const updatedConversationsGroupedByTwin = prev.map((c) => {
        if (c.twin.id === twin.id) {
          return {
            ...c,
            twin_conversations: [twinConversation, ...c.twin_conversations],
          }
        }
        return c
      })

      return updatedConversationsGroupedByTwin
    })
  }

  if (isMobile) {
    // depending on if the conversation id is present or if the twin id is present
    if (conversationId || twinIdFromParams) {
      return (
        <Content
          tabIndex={tabIndex}
          contextSources={contextSources}
          setContextSources={setContextSources}
          verboseOutput={verboseOutput}
          setVerboseOutput={setVerboseOutput}
          isSubmitting={isSubmitting}
          setIsSubmitting={setIsSubmitting}
          conversation={conversation || undefined}
          isLoadingConversationMessages={isLoadingConversationMessages}
          hydrateConversationMessages={hydrateConversationMessages}
          hydrateConversationMessage={hydrateConversationMessage}
          conversationMessages={conversationMessages}
          setConversationId={setConversationId}
          setIsCreatingNewConversation={setIsCreatingNewConversation}
          hydrateRecentConversations={hydrateRecentConversations}
          hydrateConversationsGroupedByTwin={hydrateConversationsGroupedByTwin}
        />
      )
    } else {
      return (
        <Sidebar
          contextSources={contextSources}
          setContextSources={setContextSources}
          verboseOutput={verboseOutput}
          setVerboseOutput={setVerboseOutput}
          tabIndex={tabIndex}
          setTabIndex={setTabIndex}
          setConversationId={setConversationId}
          conversationId={conversationId}
          setConversation={setConversation}
        />
      )
    }
  }

  return (
    <Stack direction="row">
      <Sidebar
        contextSources={contextSources}
        setContextSources={setContextSources}
        verboseOutput={verboseOutput}
        setVerboseOutput={setVerboseOutput}
        tabIndex={tabIndex}
        setTabIndex={setTabIndex}
        setConversationId={setConversationId}
        conversationId={conversationId}
        setConversation={setConversation}
      />
      <Content
        tabIndex={tabIndex}
        contextSources={contextSources}
        setContextSources={setContextSources}
        verboseOutput={verboseOutput}
        setVerboseOutput={setVerboseOutput}
        isSubmitting={isSubmitting}
        setIsSubmitting={setIsSubmitting}
        conversation={conversation || undefined}
        isLoadingConversationMessages={isLoadingConversationMessages}
        hydrateConversationMessages={hydrateConversationMessages}
        hydrateConversationMessage={hydrateConversationMessage}
        conversationMessages={conversationMessages}
        setConversationId={setConversationId}
        setIsCreatingNewConversation={setIsCreatingNewConversation}
        hydrateRecentConversations={hydrateRecentConversations}
        hydrateConversationsGroupedByTwin={hydrateConversationsGroupedByTwin}
      />
    </Stack>
  )
}

export default JelledBots
